import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Download, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const statusColor = (s) => {
  if (s === 'paid') return 'bg-emerald-100 text-emerald-700';
  if (s === 'overdue') return 'bg-red-100 text-red-700';
  if (s === 'pending' || s === 'unpaid') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
};

export default function CustomerInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofDialog, setProofDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/customer/invoices')
      .then(r => setInvoices(r.data.invoices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const handleUploadProof = async () => {
    if (!proofUrl.trim()) { toast.error('Masukkan URL bukti pembayaran'); return; }
    setSubmitting(true);
    try {
      await api.post(`/customer/invoices/${selectedInvoice.id}/payment-proof`, { proof_url: proofUrl });
      toast.success('Bukti pembayaran berhasil dikirim');
      setProofDialog(false);
      setProofUrl('');
      const res = await api.get('/customer/invoices');
      setInvoices(res.data.invoices || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengirim bukti pembayaran');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-invoices-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Invoice Saya</h1>
        <p className="text-sm text-slate-500 mt-1">Riwayat tagihan dan status pembayaran</p>
      </div>

      {invoices.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada invoice</h3>
            <p className="text-sm text-slate-500">Invoice dari pembelian Anda akan muncul di sini</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <Card key={inv.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <FileText size={20} className="text-[var(--store-primary, #2D6CDF)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 font-mono text-sm">{inv.invoice_number || inv.id?.slice(0, 8)}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{inv.description || inv.notes || 'Invoice'}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Due: {inv.due_date ? new Date(inv.due_date).toLocaleDateString('id-ID') : '-'}
                        {' · '}Dibuat: {new Date(inv.created_at).toLocaleDateString('id-ID')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="font-bold text-slate-900 text-lg">Rp {fmt(inv.total || inv.amount)}</p>
                    <Badge className={statusColor(inv.status)}>{inv.status}</Badge>
                    <div className="flex gap-2 mt-1">
                      {inv.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2 border-[var(--store-primary, #2D6CDF)] text-[var(--store-primary, #2D6CDF)]"
                          onClick={() => { setSelectedInvoice(inv); setProofUrl(''); setProofDialog(true); }}
                        >
                          <Upload size={12} className="mr-1" /> Bukti Bayar
                        </Button>
                      )}
                      {inv.payment_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2"
                          onClick={() => window.open(inv.payment_url, '_blank')}
                        >
                          <ExternalLink size={12} className="mr-1" /> Bayar
                        </Button>
                      )}
                    </div>
                    {inv.proof_url && (
                      <a href={inv.proof_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                        <Download size={12} /> Lihat bukti bayar
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={proofDialog} onOpenChange={setProofDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Bukti Pembayaran</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">Invoice: <span className="font-mono font-semibold">{selectedInvoice?.invoice_number}</span></p>
            <p className="text-sm text-slate-600">Total: <span className="font-bold">Rp {fmt(selectedInvoice?.total || selectedInvoice?.amount)}</span></p>
            <div>
              <Label htmlFor="proof-url">URL Bukti Pembayaran</Label>
              <Input
                id="proof-url"
                placeholder="https://i.imgur.com/... atau link Google Drive"
                value={proofUrl}
                onChange={e => setProofUrl(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">Upload foto/screenshot ke layanan hosting (Imgur, Google Drive, dll) lalu paste URL-nya di sini.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofDialog(false)}>Batal</Button>
            <Button onClick={handleUploadProof} disabled={submitting} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {submitting ? 'Mengirim...' : 'Kirim Bukti'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
